/**
 * AURA AI Assistant — Suggestion Registry (Phase 2 implementation).
 *
 * Resolves context-aware suggestions for the right-side AI panel via a
 * cascading lookup:
 *
 *   1. Screen-specific override (longest pathname-prefix match wins)
 *   2. Role default (DashboardRoleKey)
 *   3. Global fallback
 *
 * Registry content is verbatim from `docs/ui-rebuild/AI-ASSISTANT-REGISTRY.md`
 * (Kabir, aura-design). Do NOT edit suggestions here — edit the spec and
 * re-sync this file.
 *
 * Route-param threading (`requiresContext`) is not yet wired. Suggestions that
 * need context keys which are not present in the pathname are filtered out.
 */
import type { DashboardRoleKey } from '../../pages/dashboard/roleLayouts';

export interface Suggestion {
  /** ≤60 chars — fits the 360px panel without wrap-chaos. */
  text: string;
  /** Credits (1–10). Matches ActionPricing scale in CLAUDE.md. */
  cost: number;
  /** Optional internal route for "Run" to navigate to after debit. */
  deepLink?: string;
  /** Optional context keys (e.g. ['dealId']) the suggestion needs from the URL. */
  requiresContext?: string[];
}

/* ---------------------------------------------------------------------- */
/*  Role defaults                                                          */
/* ---------------------------------------------------------------------- */

const ROLE_DEFAULTS: Record<DashboardRoleKey, Suggestion[]> = {
  OWNER: [
    { text: 'Summarise my business this week', cost: 8, deepLink: '/dashboard?ai=weekly-summary' },
    { text: 'Flag top 3 deals at risk', cost: 3 },
    { text: 'Cash position snapshot', cost: 2 },
    { text: 'GST deadlines this week', cost: 2 },
    { text: 'Which dept is slipping KPIs?', cost: 5 },
    { text: 'Predict month-end revenue', cost: 5 },
  ],
  SALES_HEAD: [
    { text: 'Summarise pipeline movement this week', cost: 5 },
    { text: 'Flag reps missing quota', cost: 3 },
    { text: 'Top loss reasons this quarter', cost: 5 },
    { text: 'Forecast next-month revenue', cost: 5 },
    { text: 'Suggest deals to escalate', cost: 3 },
  ],
  SALES_MANAGER: [
    { text: "Summarise my team's week", cost: 5 },
    { text: 'Flag deals slipping in my team', cost: 3 },
    { text: 'Rank reps by conversion', cost: 3 },
    { text: 'Draft coaching note for lowest rep', cost: 5 },
    { text: 'Suggest next deals to push', cost: 3 },
  ],
  /* Apr 24, 2026 — FIELD + OFFICE merged into SALES_EXECUTIVE.
   * Picked the richer FIELD set and folded in the two most distinct
   * OFFICE prompts (stale-quote follow-up, inbound-lead triage). */
  SALES_EXECUTIVE: [
    { text: 'Plan my route for today', cost: 3 },
    { text: "Draft follow-up for yesterday's visits", cost: 4 },
    { text: 'Score my top 5 leads', cost: 3 },
    { text: 'Draft quote follow-up for stale proposals', cost: 4 },
    { text: "Summarise today's inbound leads", cost: 3 },
    { text: 'Suggest next deal to call', cost: 2 },
  ],
  PRODUCTION_HEAD: [
    { text: 'Bottleneck analysis — which work centres?', cost: 5, deepLink: '/manufacturing/process-kanban?ai=bottleneck' },
    { text: "Summarise today's shift performance", cost: 5 },
    { text: 'Predict orders at risk of missing SLA', cost: 5 },
    { text: 'QC pass-rate delta vs last week', cost: 3 },
    { text: 'Flag RM shortages blocking production', cost: 3 },
  ],
  PRODUCTION_SUPERVISOR: [
    { text: 'Predict my shift output today', cost: 3 },
    { text: 'Summarise rejections this shift', cost: 3 },
    { text: 'Flag workers below target', cost: 2 },
    { text: 'Is material ready for next queue?', cost: 2 },
  ],
  INVENTORY_HEAD: [
    { text: 'Flag SKUs below safety stock', cost: 2 },
    { text: 'Suggest SKUs to reorder today', cost: 3 },
    { text: "Summarise week's inward vs outward", cost: 5 },
    { text: 'Flag slow-moving inventory value', cost: 3 },
    { text: 'Predict stock-out risk next 7 days', cost: 5 },
  ],
  PURCHASE_HEAD: [
    { text: 'Rank vendors by on-time %', cost: 3 },
    { text: 'Flag RFQs awaiting award >48h', cost: 2 },
    { text: 'Suggest split-award for current RFQ', cost: 5 },
    { text: 'Summarise spend vs budget (MTD)', cost: 3 },
    { text: 'Flag price-change alerts this week', cost: 2 },
  ],
  SERVICE_MANAGER: [
    { text: 'Flag SLA breaches this week', cost: 3 },
    { text: 'Summarise tickets by technician', cost: 3 },
    { text: "Predict tomorrow's ticket load", cost: 5 },
    { text: 'Suggest escalations (aging >72h)', cost: 3 },
    { text: 'Warranty claim rate vs last month', cost: 3 },
  ],
  SERVICE_TECHNICIAN: [
    { text: "Parts to carry for today's visits", cost: 2 },
    { text: 'Summarise my route for today', cost: 3 },
    { text: 'Draft completion note for last ticket', cost: 4 },
    { text: "Troubleshoot this unit's last 3 issues", cost: 3, requiresContext: ['unitId'] },
  ],
  FINANCE_MANAGER: [
    { text: 'Draft GSTR-1 summary for this month', cost: 8, deepLink: '/gst/filing?ai=gstr1' },
    { text: 'Summarise P&L — month to date', cost: 5, deepLink: '/accounting?tab=pnl' },
    { text: 'Flag invoices overdue >30 days', cost: 2 },
    { text: 'Draft reminder for top 3 debtors', cost: 5 },
    { text: "Bank recon — what's unmatched?", cost: 3 },
  ],
  TELECALLER: [
    { text: 'Best opening line for next call', cost: 3 },
    { text: 'Summarise my day so far', cost: 2 },
    { text: 'Draft WhatsApp follow-up for last call', cost: 3 },
    { text: 'Score my remaining queue', cost: 2 },
  ],
  WAREHOUSE_EXECUTIVE: [
    { text: "Summarise today's GRN queue", cost: 2 },
    { text: 'Flag pending dispatches by age', cost: 2 },
    { text: 'Cycle-count anomalies this week', cost: 3 },
    { text: "Predict tomorrow's inbound volume", cost: 3 },
  ],
  ACCOUNTS_EXECUTIVE: [
    { text: 'Summarise my approval queue', cost: 2 },
    { text: 'Flag invoices missing HSN/SAC', cost: 2 },
    { text: 'Draft 3-way match exceptions note', cost: 4 },
    { text: 'GST draft lines needing review', cost: 3 },
  ],
  FIELD_PARTNER: [
    { text: 'Summarise my installations this week', cost: 3 },
    { text: 'Flag claims pending approval', cost: 2 },
    { text: 'Predict my payout this cycle', cost: 3 },
    { text: 'Draft status note for open jobs', cost: 4 },
  ],
  // Apr 26, 2026 (Squad-B) — Lead Manager + Backend Staff role defaults.
  LEAD_MANAGER: [
    { text: 'Summarise unverified-lead inbox', cost: 3, deepLink: '/leads/unverified' },
    { text: 'Flag drop-offs at the Application step', cost: 3 },
    { text: 'Suggest team for ready-to-assign leads', cost: 4 },
    { text: 'Detect duplicate or stale leads', cost: 3 },
    { text: 'Draft reassignment note for approval', cost: 4 },
  ],
  BACKEND_STAFF: [
    { text: "Summarise today's call queue", cost: 3 },
    { text: 'Flag leads needing follow-up today', cost: 2 },
    { text: 'Suggest next 5 leads to call', cost: 3 },
    { text: 'Draft call note for last interaction', cost: 4 },
    { text: 'Convert hottest lead to a deal', cost: 3, deepLink: '/deals?intent=convert' },
  ],
};

/* ---------------------------------------------------------------------- */
/*  Screen overrides                                                       */
/* ---------------------------------------------------------------------- */

/**
 * Pathname-prefix → suggestions. Sorted longest-first so specific prefixes
 * win over general ones (e.g. `/manufacturing/qc` beats `/manufacturing`).
 *
 * Special case: `/analytics` intentionally has an EMPTY suggestions array
 * to short-circuit cascade to shorter prefixes — the resolver treats a
 * matched-but-empty override as "defer to role default". (Spec marker.)
 */
const RAW_SCREEN_OVERRIDES: Array<{ prefix: string; suggestions: Suggestion[] }> = [
  {
    prefix: '/manufacturing/dashboard',
    suggestions: [
      { text: "Bottleneck analysis for today's shift", cost: 5 },
      { text: "Predict orders missing today's target", cost: 5 },
      { text: 'QC reject delta vs last week', cost: 3 },
      { text: 'Flag RM shortages blocking output', cost: 3 },
    ],
  },
  {
    prefix: '/manufacturing/qc',
    suggestions: [
      { text: 'Historical pass/fail for this product', cost: 3, requiresContext: ['productId'] },
      { text: 'Flag operators with high reject rate', cost: 3 },
      { text: 'Predict QC load for next shift', cost: 3 },
      { text: 'Summarise rejections this week', cost: 3 },
    ],
  },
  {
    prefix: '/manufacturing/process-kanban',
    suggestions: [
      { text: 'Which station is the bottleneck now?', cost: 3 },
      { text: 'Predict queue completion time', cost: 5 },
      { text: 'Flag stalled work orders (>4h)', cost: 2 },
    ],
  },
  {
    prefix: '/manufacturing/planning',
    suggestions: [
      { text: 'Suggest optimal sequence for this week', cost: 5 },
      { text: 'Flag SOs at risk of missing dispatch date', cost: 3 },
      { text: 'Forecast capacity utilisation', cost: 5 },
    ],
  },
  {
    prefix: '/deals',
    suggestions: [
      { text: 'Score next deal I should push', cost: 3 },
      { text: 'Summarise pipeline movement this week', cost: 5 },
      { text: 'Flag deals with no activity >7 days', cost: 2 },
      { text: 'Top loss reasons this quarter', cost: 5 },
    ],
  },
  {
    prefix: '/proposals',
    suggestions: [
      { text: 'Flag proposals stale >14 days', cost: 2 },
      { text: 'Draft follow-up for top 3', cost: 5 },
      { text: 'Summarise proposal status by rep', cost: 3 },
    ],
  },
  {
    prefix: '/invoices',
    suggestions: [
      { text: 'Flag invoices overdue >30 days', cost: 2 },
      { text: 'Draft reminder for top 3 unpaid', cost: 5 },
      { text: 'Summarise collections this week', cost: 3 },
      { text: "Predict this month's collection total", cost: 5 },
    ],
  },
  {
    prefix: '/payments',
    suggestions: [
      { text: 'Flag payments awaiting allocation', cost: 2 },
      { text: 'Summarise collections this week', cost: 3 },
      { text: 'Draft receipt for top 3 pending', cost: 4 },
    ],
  },
  {
    prefix: '/expenses',
    suggestions: [
      { text: 'Flag expenses awaiting my approval', cost: 2 },
      { text: 'Summarise expense categories (MTD)', cost: 3 },
      { text: 'Detect duplicate expense claims', cost: 3 },
    ],
  },
  {
    prefix: '/accounting',
    suggestions: [
      { text: 'GSTR-1 draft lines needing review', cost: 5 },
      { text: 'Top 5 outstanding receivables', cost: 3 },
      { text: 'P&L summary — month to date', cost: 5 },
      { text: "Bank recon — what's unmatched?", cost: 3 },
    ],
  },
  {
    prefix: '/gst/filing',
    suggestions: [
      { text: 'Draft GSTR-1 summary for this month', cost: 8 },
      { text: 'Draft GSTR-3B summary', cost: 8 },
      { text: 'Reconciliation exceptions this quarter', cost: 5 },
      { text: 'Predict GST liability next month', cost: 5 },
    ],
  },
  {
    prefix: '/gst/e-invoices',
    suggestions: [
      { text: 'Flag invoices pending IRN', cost: 2 },
      { text: "Summarise today's e-invoice success rate", cost: 3 },
    ],
  },
  {
    prefix: '/gst/e-waybills',
    suggestions: [
      { text: 'Flag shipments missing e-way bill', cost: 2 },
      { text: "Predict today's e-way bill volume", cost: 3 },
    ],
  },
  {
    prefix: '/inventory',
    suggestions: [
      { text: 'SKUs below safety stock', cost: 2 },
      { text: 'Suggest smart reorder list', cost: 3 },
      { text: 'Predict stock-outs next 7 days', cost: 5 },
      { text: 'Flag slow-moving value', cost: 3 },
    ],
  },
  {
    prefix: '/warehouse/inward',
    suggestions: [
      { text: "Discrepancy count in today's GRN", cost: 2 },
      { text: "Forecast tomorrow's inbound volume", cost: 3 },
      { text: 'Flag vendor GRN delays', cost: 3 },
    ],
  },
  {
    prefix: '/warehouse/dispatch-dashboard',
    suggestions: [
      { text: 'Unshipped SOs by age', cost: 2 },
      { text: 'Predict EOD dispatch capacity', cost: 5 },
      { text: 'Flag dispatches missing documents', cost: 2 },
    ],
  },
  {
    prefix: '/warehouse/stock-audit',
    suggestions: [
      { text: 'Summarise variance vs system count', cost: 3 },
      { text: 'Flag SKUs with repeated variance', cost: 3 },
    ],
  },
  {
    prefix: '/purchase-orders',
    suggestions: [
      { text: 'Top 3 vendors by on-time %', cost: 3 },
      { text: 'Suggest split-award for open RFQ', cost: 5 },
      { text: 'Flag POs overdue for delivery', cost: 2 },
    ],
  },
  {
    prefix: '/vendors',
    suggestions: [
      { text: 'Rank vendors by performance', cost: 3 },
      { text: 'Flag vendors with price increases', cost: 3 },
    ],
  },
  {
    prefix: '/quotation-jobs',
    suggestions: [
      { text: 'Summarise vendor rate spread for this RFQ', cost: 3 },
      { text: 'Suggest best award strategy', cost: 5 },
      { text: 'Flag RFQs awaiting award >48h', cost: 2 },
    ],
  },
  {
    prefix: '/so-po-mapping',
    suggestions: [
      { text: 'Unmapped SO line items', cost: 2 },
      { text: 'Red-bucket drill-down (>48h)', cost: 3 },
      { text: 'Predict which SOs will turn red', cost: 5 },
    ],
  },
  {
    prefix: '/projects',
    suggestions: [
      { text: 'Flag projects at risk of milestone slip', cost: 3 },
      { text: 'Resource conflicts this week', cost: 3 },
      { text: 'Summarise project velocity', cost: 5 },
    ],
  },
  {
    prefix: '/tasks',
    suggestions: [
      { text: 'Summarise my overdue tasks', cost: 3 },
      { text: "Draft daily standup from today's activity", cost: 5 },
      { text: 'Suggest tasks to delegate', cost: 3 },
    ],
  },
  {
    prefix: '/service-tickets',
    suggestions: [
      { text: 'Flag SLA breaches this week', cost: 3 },
      { text: 'Escalate top 3 aging tickets', cost: 3 },
      { text: "Predict today's ticket load", cost: 5 },
      { text: 'Summarise tickets by technician', cost: 3 },
    ],
  },
  {
    prefix: '/warranties',
    suggestions: [
      { text: 'Flag warranty claims pending review', cost: 2 },
      { text: "Predict this month's claim volume", cost: 3 },
      { text: 'Summarise claim rate by product', cost: 3 },
    ],
  },
  {
    prefix: '/customers',
    suggestions: [
      { text: 'Find stale contacts (>90d no activity)', cost: 2 },
      { text: 'Suggest accounts for upsell', cost: 3 },
      { text: 'Score customer health', cost: 3 },
      { text: 'Flag accounts with open issues', cost: 2 },
    ],
  },
  {
    prefix: '/accounts',
    suggestions: [
      { text: 'Find stale contacts (>90d no activity)', cost: 2 },
      { text: 'Suggest accounts for upsell', cost: 3 },
      { text: 'Score customer health', cost: 3 },
      { text: 'Flag accounts with open issues', cost: 2 },
    ],
  },
  {
    prefix: '/contacts',
    suggestions: [
      { text: 'Find stale contacts (>90d no activity)', cost: 2 },
      { text: 'Suggest accounts for upsell', cost: 3 },
      { text: 'Score customer health', cost: 3 },
      { text: 'Flag accounts with open issues', cost: 2 },
    ],
  },
  {
    prefix: '/leads',
    suggestions: [
      { text: "Score today's new leads", cost: 3 },
      { text: 'Summarise top sources this month', cost: 3 },
      { text: 'Suggest next lead to call', cost: 2 },
      { text: 'Draft intro email for top 3 leads', cost: 5 },
    ],
  },
  // /analytics is role-aware — empty array = resolver cascades to role default.
  {
    prefix: '/analytics',
    suggestions: [],
  },
  {
    prefix: '/field-partners',
    suggestions: [
      { text: 'Partner incentive leaderboard', cost: 3 },
      { text: 'Pending KYC queue', cost: 2 },
      { text: 'Flag fraud-review pending partners', cost: 2 },
    ],
  },
  {
    prefix: '/employees',
    suggestions: [
      { text: 'Leave requests awaiting my approval', cost: 2 },
      { text: 'Attendance anomalies this week', cost: 3 },
      { text: 'Summarise team utilisation', cost: 3 },
    ],
  },
  {
    prefix: '/credits',
    suggestions: [
      { text: 'Which modules are burning most credits?', cost: 2 },
      { text: 'Forecast this-month credit burn', cost: 3 },
      { text: 'Flag users with high consumption', cost: 2 },
    ],
  },
  {
    prefix: '/telecaller',
    suggestions: [
      { text: 'Best opening line for next call', cost: 3 },
      { text: 'Summarise my day so far', cost: 2 },
      { text: 'Draft WhatsApp follow-up for last call', cost: 3 },
      { text: 'Score my remaining queue', cost: 2 },
    ],
  },
  {
    prefix: '/call-logs',
    suggestions: [
      { text: 'Best opening line for next call', cost: 3 },
      { text: 'Summarise my day so far', cost: 2 },
      { text: 'Draft WhatsApp follow-up for last call', cost: 3 },
      { text: 'Score my remaining queue', cost: 2 },
    ],
  },
  {
    prefix: '/executive/sales/call-queue',
    suggestions: [
      { text: 'Best opening line for next call', cost: 3 },
      { text: 'Score my remaining queue', cost: 2 },
      { text: 'Draft WhatsApp follow-up for last call', cost: 3 },
    ],
  },
  {
    prefix: '/executive/service/tickets',
    suggestions: [
      { text: "Parts to carry for today's visits", cost: 2 },
      { text: 'Summarise my route for today', cost: 3 },
      { text: 'Draft completion note for last ticket', cost: 4 },
    ],
  },
  {
    prefix: '/executive/production/work-orders',
    suggestions: [
      { text: 'Predict my shift output today', cost: 3 },
      { text: 'Is material ready for next WO?', cost: 2 },
      { text: 'Flag WOs behind schedule', cost: 3 },
    ],
  },
  {
    prefix: '/executive/warehouse/grn',
    suggestions: [
      { text: "Summarise today's GRN queue", cost: 2 },
      { text: 'Flag discrepancies for review', cost: 2 },
      { text: 'Predict inbound volume next 2h', cost: 3 },
    ],
  },
  {
    prefix: '/executive/accounts/match-queue',
    suggestions: [
      { text: 'Summarise my match queue', cost: 2 },
      { text: 'Flag 3-way match exceptions', cost: 3 },
      { text: 'Draft note for exceptions', cost: 4 },
    ],
  },
  {
    prefix: '/executive/field-partner/claims',
    suggestions: [
      { text: 'Summarise my open claims', cost: 2 },
      { text: 'Flag claims pending approval', cost: 2 },
      { text: 'Predict my payout this cycle', cost: 3 },
    ],
  },
];

// Sort longest-prefix-first so `/manufacturing/qc` matches before `/manufacturing`.
const SCREEN_OVERRIDES = [...RAW_SCREEN_OVERRIDES].sort(
  (a, b) => b.prefix.length - a.prefix.length,
);

/* ---------------------------------------------------------------------- */
/*  Global fallback                                                        */
/* ---------------------------------------------------------------------- */

const GLOBAL_FALLBACK: Suggestion[] = [
  { text: 'What can AURA Assistant do?', cost: 1 },
  { text: "Show me my role's shortcuts", cost: 1 },
  { text: 'Summarise my day so far', cost: 3 },
  { text: 'What should I do next?', cost: 3 },
];

/* ---------------------------------------------------------------------- */
/*  Screen labels (panel header subtitle)                                  */
/* ---------------------------------------------------------------------- */

const RAW_SCREEN_LABELS: Array<{ prefix: string; label: string }> = [
  { prefix: '/dashboard', label: 'Dashboard' },
  { prefix: '/manufacturing/dashboard', label: 'Production · Overview' },
  { prefix: '/manufacturing/qc', label: 'Production · QC Management' },
  { prefix: '/manufacturing/process-kanban', label: 'Production · Process Kanban' },
  { prefix: '/manufacturing/planning', label: 'Production · Planning' },
  { prefix: '/manufacturing', label: 'Production' },
  { prefix: '/deals', label: 'Deals' },
  { prefix: '/proposals', label: 'Proposals' },
  { prefix: '/sales-orders', label: 'Sales Orders' },
  { prefix: '/invoices', label: 'Invoices' },
  { prefix: '/payments', label: 'Payments' },
  { prefix: '/expenses', label: 'Expenses' },
  { prefix: '/accounting', label: 'Accounting' },
  { prefix: '/gst/e-invoices', label: 'GST · e-Invoice' },
  { prefix: '/gst/e-waybills', label: 'GST · e-Way Bill' },
  { prefix: '/gst/filing', label: 'GST · Filing' },
  { prefix: '/inventory', label: 'Inventory' },
  { prefix: '/warehouse/inward', label: 'Warehouse · Inward' },
  { prefix: '/warehouse/dispatch-dashboard', label: 'Warehouse · Dispatch' },
  { prefix: '/warehouse/stock-audit', label: 'Warehouse · Stock Audit' },
  { prefix: '/warehouse', label: 'Warehouse' },
  { prefix: '/purchase-orders', label: 'Purchase Orders' },
  { prefix: '/vendors', label: 'Vendors' },
  { prefix: '/quotation-jobs', label: 'Quotation Jobs' },
  { prefix: '/so-po-mapping', label: 'SO-PO Mapping' },
  { prefix: '/projects', label: 'Projects' },
  { prefix: '/tasks', label: 'Tasks' },
  { prefix: '/service-tickets', label: 'Service Tickets' },
  { prefix: '/warranties', label: 'Warranties' },
  { prefix: '/customers', label: 'CRM' },
  { prefix: '/accounts', label: 'CRM' },
  { prefix: '/contacts', label: 'CRM' },
  { prefix: '/leads', label: 'Leads' },
  { prefix: '/analytics', label: 'Analytics' },
  { prefix: '/field-partners', label: 'Field Partners' },
  { prefix: '/employees', label: 'Team' },
  { prefix: '/credits', label: 'AI Credits' },
  { prefix: '/telecaller', label: 'Telecaller' },
  { prefix: '/call-logs', label: 'Telecaller' },
  { prefix: '/executive', label: 'My Desk' },
];

const SCREEN_LABELS = [...RAW_SCREEN_LABELS].sort(
  (a, b) => b.prefix.length - a.prefix.length,
);

/* ---------------------------------------------------------------------- */
/*  Detail-route label exceptions (handled by suffix check)                */
/* ---------------------------------------------------------------------- */

/**
 * Detail-route overrides for the subtitle label. Applied BEFORE the
 * prefix lookup so `/deals/abc-123` → "Deal Detail" not "Deals".
 */
const DETAIL_LABELS: Array<{ match: RegExp; label: string }> = [
  { match: /^\/deals\/[^/]+\/?$/, label: 'Deal Detail' },
  { match: /^\/invoices\/[^/]+\/?$/, label: 'Invoice Editor' },
  { match: /^\/service-tickets\/[^/]+\/?$/, label: 'Ticket Detail' },
];

/* ---------------------------------------------------------------------- */
/*  Context-availability filter                                            */
/* ---------------------------------------------------------------------- */

/**
 * Map `requiresContext` key → pathname regex. If the pathname contains an
 * ID segment for the relevant detail route we treat the context as present;
 * otherwise the suggestion is filtered out (route-param threading isn't
 * wired yet — spec Section "Roles where context is thin").
 */
const CONTEXT_ROUTE_PATTERNS: Record<string, RegExp> = {
  dealId: /^\/deals\/[^/]+/,
  invoiceId: /^\/invoices\/[^/]+/,
  ticketId: /^\/service-tickets\/[^/]+/,
  unitId: /^\/service-tickets\/[^/]+/, // unit derived from ticket route for now
  productId: /^\/products\/[^/]+/,
};

function hasContextFor(pathname: string, keys: string[]): boolean {
  return keys.every((k) => {
    const pattern = CONTEXT_ROUTE_PATTERNS[k];
    // Unknown context keys → always filter out (safer default).
    if (!pattern) return false;
    return pattern.test(pathname);
  });
}

function filterByContext(pathname: string, list: Suggestion[]): Suggestion[] {
  return list.filter((s) => !s.requiresContext || hasContextFor(pathname, s.requiresContext));
}

/* ---------------------------------------------------------------------- */
/*  Public API                                                             */
/* ---------------------------------------------------------------------- */

/**
 * Resolve the 4–6 suggestions to show for a given (role, pathname).
 *
 * Order:
 *   1. Longest-matching screen-override prefix (non-empty) → those
 *   2. Role-default list
 *   3. Global fallback (unknown role)
 *
 * `requiresContext` suggestions whose keys are not present in the
 * pathname are filtered out.
 */
export function resolveSuggestions(
  role: DashboardRoleKey | null | undefined,
  pathname: string,
): Suggestion[] {
  const normalisedPath = pathname || '/';

  // 1. Screen override (longest prefix wins; empty array → defer to role).
  for (const entry of SCREEN_OVERRIDES) {
    if (normalisedPath.startsWith(entry.prefix) && entry.suggestions.length > 0) {
      return filterByContext(normalisedPath, entry.suggestions).slice(0, 6);
    }
  }

  // 2. Role default.
  if (role && ROLE_DEFAULTS[role]) {
    return filterByContext(normalisedPath, ROLE_DEFAULTS[role]).slice(0, 6);
  }

  // 3. Global fallback.
  return filterByContext(normalisedPath, GLOBAL_FALLBACK).slice(0, 6);
}

/**
 * Resolve a human-readable screen label for the panel header subtitle
 * (e.g. `"Production · QC Management"`).
 *
 * Detail routes (e.g. `/deals/abc-123`) return a detail-specific label.
 * Fallback: titlecased first path segment.
 */
export function resolveScreenLabel(pathname: string): string {
  const normalisedPath = pathname || '/';

  // Detail-route exceptions take precedence.
  for (const { match, label } of DETAIL_LABELS) {
    if (match.test(normalisedPath)) return label;
  }

  for (const { prefix, label } of SCREEN_LABELS) {
    if (normalisedPath.startsWith(prefix)) return label;
  }

  // Fallback — titlecased slug.
  if (normalisedPath === '/' || normalisedPath === '') return 'Dashboard';
  const slug = normalisedPath.replace(/^\/+/, '').split('/')[0] ?? '';
  if (!slug) return 'App';
  return slug.charAt(0).toUpperCase() + slug.slice(1).replace(/-/g, ' ');
}

/**
 * Human short label for a role. Used alongside `resolveScreenLabel`
 * to render `"Production Head · QC Management"` in the panel header.
 */
export function resolveRoleShortLabel(role: DashboardRoleKey | null | undefined): string {
  if (!role) return 'AURA';
  const map: Record<DashboardRoleKey, string> = {
    OWNER: 'Owner',
    SALES_HEAD: 'Sales Head',
    SALES_MANAGER: 'Sales Manager',
    SALES_EXECUTIVE: 'Sales Executive',
    PRODUCTION_HEAD: 'Production Head',
    PRODUCTION_SUPERVISOR: 'Production Supervisor',
    INVENTORY_HEAD: 'Inventory Head',
    PURCHASE_HEAD: 'Purchase Head',
    SERVICE_MANAGER: 'Service Manager',
    SERVICE_TECHNICIAN: 'Service Technician',
    FINANCE_MANAGER: 'Finance Manager',
    TELECALLER: 'Telecaller',
    WAREHOUSE_EXECUTIVE: 'Warehouse Exec',
    ACCOUNTS_EXECUTIVE: 'Accounts Exec',
    FIELD_PARTNER: 'Field Partner',
    LEAD_MANAGER: 'Lead Manager',
    BACKEND_STAFF: 'Backend / Tele',
  };
  return map[role];
}
